import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Pagination component for navigating through paged data
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current page number (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalItems - Total number of items
 * @param {number} props.pageSize - Items per page
 * @param {function} props.onPageChange - Callback when page changes
 * @param {function} props.onPageSizeChange - Callback when page size changes (optional)
 * @param {Array<number>} props.pageSizeOptions - Available page sizes (optional)
 * @param {boolean} props.showPageSizeSelector - Show page size selector (optional)
 * @param {boolean} props.showItemCount - Show total item count (optional)
 * @param {string} props.className - Additional classes (optional)
 */
export const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = false,
  showItemCount = true,
  className,
}) => {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Calculate showing range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show current page with surrounding pages
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      // Adjust if at the start
      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      }
      // Adjust if at the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1 && !showPageSizeSelector) {
    return null; // Don't render if only one page
  }

  return (
    <div className={cn('flex items-center justify-between gap-4 px-2 py-3', className)}>
      {/* Item count */}
      {showItemCount && (
        <div className="text-sm text-text-secondary">
          {totalItems === 0 ? (
            'No items'
          ) : (
            <>
              Showing <span className="font-medium text-text-primary">{startItem}</span> to{' '}
              <span className="font-medium text-text-primary">{endItem}</span> of{' '}
              <span className="font-medium text-text-primary">{totalItems}</span> items
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {/* Page size selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2 mr-4">
            <span className="text-sm text-text-secondary">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-sm bg-surface border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={!canGoPrevious}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              canGoPrevious
                ? 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                : 'text-text-muted cursor-not-allowed'
            )}
            aria-label="Go to first page"
          >
            <ChevronsLeft size={18} />
          </button>

          {/* Previous page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrevious}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              canGoPrevious
                ? 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                : 'text-text-muted cursor-not-allowed'
            )}
            aria-label="Go to previous page"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-1">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-text-secondary">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={cn(
                    'min-w-[32px] h-8 px-2 text-sm rounded-md transition-colors',
                    page === currentPage
                      ? 'bg-primary text-white font-medium'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                  )}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              canGoNext
                ? 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                : 'text-text-muted cursor-not-allowed'
            )}
            aria-label="Go to next page"
          >
            <ChevronRight size={18} />
          </button>

          {/* Last page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={!canGoNext}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              canGoNext
                ? 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                : 'text-text-muted cursor-not-allowed'
            )}
            aria-label="Go to last page"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Simple pagination for infinite scroll / load more pattern
 */
export const LoadMoreButton = ({
  onClick,
  loading = false,
  hasMore = true,
  className,
  children = 'Load More',
}) => {
  if (!hasMore) return null;

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={cn(
        'w-full py-3 text-sm font-medium text-text-secondary hover:text-text-primary',
        'border border-border rounded-lg hover:bg-surface-light transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="animate-spin">⟳</span>
          Loading...
        </span>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Calculate pagination info from total count
 */
export const calculatePagination = (page, pageSize, totalCount) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasMore = page < totalPages;
  const from = (page - 1) * pageSize;
  const to = Math.min(from + pageSize - 1, totalCount - 1);

  return {
    page,
    pageSize,
    totalPages,
    totalCount,
    hasMore,
    from,
    to,
  };
};

export default Pagination;
